// CLI-local, deliberately-loose mirrors of the backend config + diff shapes.
//
// The CLI must not depend on @rekor/shared at runtime (it would pull the whole shared bundle
// into the published package). These structural types only need to serialize/deserialize YAML
// and render a diff, so they stay minimal. Source of truth: workers/shared/src/types/promotion.ts
// and the per-entity row shapes — the config round-trip test guards against drift.

export interface RecordTypeConfig {
  id: string;
  name: string;
  description?: string;
  icon?: string;
  color?: string;
  json_schema: Record<string, unknown>;
  ui?: Record<string, unknown>;
  sources?: unknown[];
}

export interface RelationshipTypeConfig {
  id: string;
  description?: string;
  data_schema?: unknown;
  source_record_types?: string[];
  target_record_types?: string[];
}

export interface InboundWebhookConfig {
  id: string;
  name: string;
  record_type_scope?: string[] | null;
  /** Inline to_rekor mapping applied to the payload before write (#454 PR2). Secret-free. */
  field_mapping?: Record<string, unknown> | null;
  /** Reuse a record_type source's field_mapping: {record_type, source} (#454 PR2). */
  source_binding?: Record<string, unknown> | null;
  /** Static-header inbound auth: {type:'static_header',header} (#470). Secret-free. */
  ingest_auth?: Record<string, unknown> | null;
  /** Hydration config: {id_path,event_path?,event_map?,default_op?} (#470 PR2). Secret-free. */
  hydration?: Record<string, unknown> | null;
  enabled?: boolean;
}

export interface TriggerConfig {
  id: string;
  name: string;
  /** Polymorphic action (#390): {type:'webhook',url} or {type:'internal_write',...}. Secret omitted. */
  action?: Record<string, unknown>;
  /** Legacy flat webhook url — kept optional for back-compat; `action` is canonical. */
  url?: string;
  events: string[];
  record_type_scope?: string[] | null;
  filter?: Record<string, unknown> | null;
  skip_inbound_webhook_writes?: boolean;
  skip_cascade_writes?: boolean;
  enabled?: boolean;
}

export interface ToolsetConfig {
  id: string;
  name: string;
  description?: string;
  tools?: unknown[];
  relationships?: unknown[];
  batch?: unknown;
  sql_query?: boolean;
}

/** A first-class Tool: one record_type + one operation, with its guard/shaping config. Secret-free. */
export interface ToolConfig {
  id: string;
  record_type: string;
  operation: string;
  description?: string;
  external_source?: string;
  writable_fields?: unknown[];
  precondition?: Record<string, unknown>;
  binding?: string;
  filterable_fields?: unknown[];
  expose_filter?: boolean;
  expose_sort?: boolean;
  default_sort?: unknown[];
  expose_limit?: boolean;
  default_limit?: number;
  expose_offset?: boolean;
  expose_fields?: boolean;
  default_fields?: string[] | string;
  agent_minimal?: boolean;
}

export interface BaseConfig {
  record_types: RecordTypeConfig[];
  relationship_types: RelationshipTypeConfig[];
  inbound_webhooks: InboundWebhookConfig[];
  triggers: TriggerConfig[];
  toolsets: ToolsetConfig[];
  tools: ToolConfig[];
}

/** Persisted in `<db-folder>/base.yaml`. `base_id` is absent for an as-yet-uncreated preview. */
export interface BaseMeta {
  base_id?: string;
  origin_base_id?: string;
  name?: string;
  description?: string;
  environment?: string;
  // #488: external integration toggle (enabled | disabled). Informational on pull — toggle a preview
  // with `rekor bases update <preview> --integrations <mode>` (preview-only, enforced server-side).
  integrations?: string;
}

export interface DiffBucket<T> {
  added: T[];
  modified: { before: T; after: T }[];
  removed: T[];
}

export interface ConfigDiff {
  record_types: DiffBucket<RecordTypeConfig>;
  relationship_types?: DiffBucket<RelationshipTypeConfig>;
  inbound_webhooks: DiffBucket<InboundWebhookConfig>;
  triggers: DiffBucket<TriggerConfig>;
  toolsets?: DiffBucket<ToolsetConfig>;
  tools?: DiffBucket<ToolConfig>;
}

/** The config entity kinds, in a stable order used for folders, files and diff rendering. */
export const ENTITY_KINDS = ['record_types', 'relationship_types', 'inbound_webhooks', 'triggers', 'toolsets', 'tools'] as const;
export type EntityKind = (typeof ENTITY_KINDS)[number];

/** Folder name on disk for each entity kind (kebab-case for the hyphenated one). */
export const ENTITY_DIRS: Record<EntityKind, string> = {
  record_types: 'record-types',
  relationship_types: 'relationship-types',
  inbound_webhooks: 'inbound-webhooks',
  triggers: 'triggers',
  toolsets: 'toolsets',
  tools: 'tools',
};

/**
 * Folder names for entity kinds that no longer exist. `writeBaseFolder` removes these on every
 * write so a pull leaves no dead-weight dirs from a superseded schema (#567). Add a name here (never
 * remove) whenever a kind is renamed/dropped — e.g. `endpoints` → `toolsets` (#562–565). Must never
 * intersect the values of `ENTITY_DIRS`.
 */
export const DEPRECATED_ENTITY_DIRS: readonly string[] = ['endpoints', 'collections'];
